import React, { useState, useMemo } from 'react';

import { Box, IconButton } from '@mui/material';
import { Combobox } from '@bestdoctor/spectrum-uikit/Combobox';
import { MultiCombobox } from '@bestdoctor/spectrum-uikit/MultiCombobox';
import { IconButton as SpectrumIconButton } from '@bestdoctor/spectrum-uikit/IconButton';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';

import { TooltipTotalStats } from 'shared/tooltip/TooltipTotalStats/TooltipTotalStats';

import { useGetStatsCompanyByPatients } from 'hooks/companies/useStatsCompanyByPatients';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as SettingsIcon } from 'assets/images/settings.svg';

import {
  Company,
  DataPeriod,
  StaffFiltersProps,
  IStatsCompanyByPatients,
  ErrorData,
} from 'interfaces/global/globalInterfaces';
import { mainFiltersContainerStyle, mainFiltersSectionStyle, mainFiltersWrapperStyle } from 'ui/patients/mainFilters';
import { calculateStatsPercentages } from 'utils/calculateStatsPercentages';
import { StatsCompanyByPatients } from 'interfaces/hooks/companiesHooksInterfaces';
import { ContractInfoModal } from 'shared/modals/ContractInfoModal/ContractInfoModal';
import { useSnackbar } from 'notistack';

export const StaffFilters: React.FC<StaffFiltersProps> = ({
  dataPeriods,
  allCompanies,

  periodInMainFilter,
  handlePeriodInMainFilter,

  selectedCompanies,
  handleSelectedCompanies,
  periodStaffAction,
  managersHR,
  isSlotsAviable,
}) => {
  const [isTooltip, setTooltip] = useState<boolean>(false);
  const [contractInfoModal, setContractInfo] = useState<boolean>(false);
  const [statsCompanyByPatients, setStatsCompanyByPatients] = useState<IStatsCompanyByPatients>({
    less_30: 0,
    men_percentage: 0,
    more_30_and_less_45: 0,
    more_45_and_less_60: 0,
    more_60: 0,
    total_registered: 0,
    women_percentage: 0,
  });

  const handleTooltipClose = (): void => {
    setTooltip(false);
  };

  const handleTooltipOpen = (): void => {
    setTooltip(!isTooltip);
  };

  const { enqueueSnackbar } = useSnackbar();

  const { isLoadingStatsCompanyByPatients, isErrorStatsCompanyByPatients } = useGetStatsCompanyByPatients({
    company: selectedCompanies ? selectedCompanies[0] : null,
    period: periodInMainFilter,
    isSlotsAviable,

    onSuccess: ({ data }: StatsCompanyByPatients) => {
      setStatsCompanyByPatients(calculateStatsPercentages(data));
    },
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const companyOptions: ComboboxOption<Company>[] = useMemo(
    () =>
      allCompanies.map((company) => ({
        label: company.title,
        value: company,
      })),
    [allCompanies]
  );

  const periodOptions: ComboboxOption<DataPeriod>[] = useMemo(
    () =>
      (selectedCompanies?.length === 1 ? dataPeriods || [] : []).map((period) => ({
        label: period.title,
        value: period,
      })),
    [dataPeriods, selectedCompanies]
  );

  const selectedCompanyOptions = useMemo(
    () =>
      companyOptions.filter((option) =>
        selectedCompanies.some((company) => company.uuid === option.value.uuid)
      ),
    [companyOptions, selectedCompanies]
  );

  const selectedPeriodOption = useMemo(() => {
    if (selectedCompanies?.length === 1 && periodInMainFilter) {
      return periodOptions.find((option) => option.value.uuid === periodInMainFilter.uuid) || null;
    }
    return null;
  }, [periodOptions, periodInMainFilter, selectedCompanies]);

  return (
    <>
      <Box component="section" sx={mainFiltersSectionStyle}>
        <Box sx={mainFiltersWrapperStyle}>
          <Box sx={{ ...mainFiltersContainerStyle, gap: '8px' }}>
            <Box sx={{ flex: 1 }}>
              <MultiCombobox<Company>
                label="Компания"
                placeholder={selectedCompanies?.length === 0 ? 'Компания' : ''}
                options={companyOptions}
                value={selectedCompanyOptions.map((opt) => opt.value)}
                searchable
                clearable
                onChange={(values) => {
                  sessionStorage.setItem('mainFilterPeriodStaff', JSON.stringify(''));

                  if (values && values.length > 0) {
                    // Проверяем, выбраны ли все компании
                    if (values.length === allCompanies.length) {
                      handleSelectedCompanies(allCompanies);
                      sessionStorage.setItem('mainFilterCompaniesStaff', JSON.stringify(allCompanies));
                    } else {
                      handleSelectedCompanies(values);
                      sessionStorage.setItem('mainFilterCompaniesStaff', JSON.stringify(values));
                    }
                  } else {
                    handleSelectedCompanies([]);
                    sessionStorage.setItem('mainFilterCompaniesStaff', JSON.stringify([]));
                  }
                }}
                size="m"
              />
            </Box>
            {periodInMainFilter?.activePeriod && isSlotsAviable && (
              <TooltipTotalStats
                open={isTooltip}
                isStats={statsCompanyByPatients}
                handleTooltipClose={handleTooltipClose}
              >
                <SpectrumIconButton
                  aria-label="Общая статистика"
                  onClick={handleTooltipOpen}
                  open={isTooltip}
                  disabled={
                    selectedCompanies?.length !== 1 ||
                    !periodInMainFilter ||
                    !!isErrorStatsCompanyByPatients ||
                    isLoadingStatsCompanyByPatients ||
                    statsCompanyByPatients.total_registered === 0
                  }
                  style={{
                    width: 48,
                    height: 48,
                    border: '1px solid rgba(255, 115, 119, 0.2)',
                    borderRadius: 14,
                    color: '#FF7377',
                    outline: 'none',
                  }}
                >
                  <SettingsIcon width={24} height={24} />
                </SpectrumIconButton>
              </TooltipTotalStats>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <Combobox<DataPeriod>
                label="Период данных"
                placeholder="Период данных"
                options={periodOptions}
                value={selectedPeriodOption?.value || undefined}
                searchable
                clearable
                disabled={selectedCompanies?.length !== 1}
                onChange={(value, option) => {
                  if (value && option) {
                    handlePeriodInMainFilter(value);
                    sessionStorage.setItem('mainFilterPeriodStaff', JSON.stringify(value));
                  } else {
                    handlePeriodInMainFilter(null);
                    sessionStorage.setItem('mainFilterPeriodStaff', JSON.stringify(''));
                  }
                }}
                size="m"
              />
            </Box>
            {isSlotsAviable && (
              <IconButton
                disabled={selectedCompanies?.length !== 1 || !periodInMainFilter}
                sx={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid rgba(255, 115, 119, 0.2)',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 115, 119, 0.1)',
                  color: '#FF7377',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 115, 119, 0.1)',
                    opacity: 0.9,
                  },
                  '&.Mui-disabled': {
                    borderColor: '#BCBEC8',
                    backgroundColor: '#F5F5F5',
                    color: '#BCBEC8',
                  },
                }}
                aria-label="Информация"
                onClick={() => setContractInfo(true)}
              >
                <InfoOutlinedIcon sx={{ fontSize: '24px' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
      <ContractInfoModal
        open={contractInfoModal}
        handleClose={() => setContractInfo(false)}
        data={periodStaffAction}
        period={periodInMainFilter}
        managersHR={managersHR}
      />
    </>
  );
};
